import React, { useEffect, useState, lazy, Suspense } from 'react';

// Comps
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';
const AddressForm = lazy(() => import('@project-stories/ecommerce/Account/AccountAddresses/AddressForm'));
import Preloader from '@arctheme-components/blocks/Preloader/Preloader';
import AccountAddresses from '@project-stories/ecommerce/Account/AccountAddresses/AccountAddresses';

// Hooks
import useCustomerAddress from '@src/hooks/useCustomerAddress';

const AccountAddressesLanding = ({ section, globals, defaultCountry, locales }) => {
  const shopifyCustomerId = (window as any).customer.id;
  const { contact } = globals;

  // States
  const [editAddress, setEditAddress] = useState(null);

  // Hooks
  const {
    fetchCustomerAddresses,
    fetchProvincesFromCountry,
    updateCustomerAddress,
    addCustomerAddress,
    removeAddress,
    resetStates,

    addresses,
    countries,
    provinces,
    fetchingAddresses,
    updatingAddress,
    updateError,
    updateSuccess,
    addingAddress,
    addError,
    addSuccess,
    deleteError
  } = useCustomerAddress();

  const defaultCountryName = countries?.find((country) => country.code === defaultCountry)?.name;

  const handleClickEditOrAdd = (event: React.ClickEvent<HTMLAnchorElement>, address: any) => {
    event?.preventDefault();

    setEditAddress(address);
  };

  const handleClickRemove = (event: React.ClickEvent<HTMLAnchorElement>, addressId: number) => {
    event.preventDefault();
    if (window.confirm(locales.addresses.deleteConfirm)) {
      removeAddress(shopifyCustomerId, addressId);
    }
  };


  const resetForm = () => {
    setEditAddress(null);
    resetStates();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const closeAddressForm = (event: React.ClickEvent<HTMLButtonElement>) => {
    event.preventDefault();
    resetForm();
  };

  useEffect(() => {
    fetchCustomerAddresses(shopifyCustomerId);
  }, []);

  return (
    <AccountSectionLayout classes={`lg:pl-16 xl:pl-[93px] hide-scrollbar max-lg:mx-auto max-lg:max-w-[600px]`}>
      <div className={'flex flex-col lg:flex-row gap-[41px] lg:gap-16 xl:gap-[93px]'}>
        <div className={'flex flex-col w-full lg:max-w-[360px] xl:max-w-[402px] gap-8'}>
          {editAddress ? (
            <Suspense>
              <AddressForm
                address={editAddress}
                countries={countries}
                provinces={provinces}
                getProvinces={fetchProvincesFromCountry}
                newForm={!editAddress.id}
                onClickCancel={closeAddressForm}
                shopifyCustomerId={shopifyCustomerId}
                updateCustomerAddress={updateCustomerAddress}
                updatingAddress={updatingAddress}
                updateError={updateError}
                updateSuccess={updateSuccess}
                addCustomerAddress={addCustomerAddress}
                addingAddress={addingAddress}
                addError={addError}
                addSuccess={addSuccess}
                locales={locales.addresses}
                resetForm={resetForm}
              />
            </Suspense>
          ) : fetchingAddresses ? (
            <></>
            // <Preloader loadingText='Loading addresses...' />
          ) : (
            <AccountAddresses
              addresses={addresses}
              onClickEditOrAdd={handleClickEditOrAdd}
              onClickRemove={handleClickRemove}
              deleteError={deleteError}
              locales={locales.addresses}
              defaultCountry={defaultCountryName}
            />
          )}
        </div>

        <div className={`flex flex-col w-full lg:flex-1`}>
          <p className={`${section.contact_us_heading_style} mb-6`}>
            Contact us
          </p>

          <div className={`flex flex-col gap-4`}>
            <p className={section.contact_us_body_style}>
              {contact?.name} <br />
              {contact?.address} <br />
              {contact?.city}, {contact?.state} {contact?.zipcode} <br />
              {contact?.country}
            </p>

            <p className={section.contact_us_body_style}>
              <a
                href={`tel:+${contact?.phone}`}
                className="underline"
              >
                {contact?.phone}
              </a>
            </p>

            <p className={section.contact_us_body_style}>
              <a
                href="/pages/contact"
                className="underline"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </AccountSectionLayout>
  );
};

export default AccountAddressesLanding;
